import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretUp, faCaretDown } from "@fortawesome/free-solid-svg-icons";
import Loading from "../layout/Loading";
import PaginationButtons from "./PaginationButtons";

const SortableDataRow = ({ rowId, className, onClick, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={className}
      onClick={onClick}
      data-dragging={isDragging ? "true" : "false"}
    >
      {children({
        dragHandleProps: { ...attributes, ...listeners },
        isDragging,
      })}
    </tr>
  );
};

const DataTable = ({
  columns,
  data,
  loading = false,
  onRowClick,
  selectableRows = false,
  onSelectedRowsChange,
  selectedRows = [],
  highlightOnHover = true,
  pointerOnHover = false,
  noDataComponent = "No data found",
  customStyles = {},
  defaultSortFieldId,
  defaultSortAsc = true,
  recordsData,
  onPageChange = null,
  truncate = "",
  baseUrl = "",
  draggableRows = false,
  rowIdKey = "id",
  onRowsReorder,
  setSortParams,
  isSortedManually = false,
}) => {
  const [sorting, setSorting] = useState(() => {
    if (defaultSortFieldId) {
      return [
        {
          id: defaultSortFieldId,
          desc: !defaultSortAsc,
        },
      ];
    }

    return [];
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const columnHelper = createColumnHelper();

  const tableColumns = useMemo(() => {
    return columns.map((col) => {
      const columnId = col.id || col.name?.toLowerCase().replace(/\s+/g, "_");

      const columnDef = {
        id: columnId,
        header: col.name,
        enableSorting: col.sortable !== false,
        sortDescFirst: col.sortDescFirst,
        size: col.width ? parseInt(col.width) : undefined,
        minSize: col.minWidth ? parseInt(col.minWidth) : undefined,
        maxSize: col.maxWidth ? parseInt(col.maxWidth) : undefined,
      };

      if (typeof col.selector === "function") {
        columnDef.accessorFn = col.selector;
      } else if (col.selector) {
        columnDef.accessorKey = col.selector;
      } else if (col.accessorKey) {
        columnDef.accessorKey = col.accessorKey;
      }

      if (col.cell) {
        columnDef.cell = ({ row }) => col.cell(row.original);
      }

      if (col.sortFunction) {
        columnDef.sortingFn = (rowA, rowB, columnId) => {
          return col.sortFunction(rowA.original, rowB.original);
        };
      }

      if (columnDef.accessorFn) {
        return columnHelper.accessor(columnDef.accessorFn, columnDef);
      } else if (columnDef.accessorKey) {
        return columnHelper.accessor(columnDef.accessorKey, columnDef);
      } else {
        return columnHelper.display(columnDef);
      }
    });
  }, [columns, columnHelper]);

  const table = useReactTable({
    data: data || [],
    columns: tableColumns,
    state: {
      sorting,
      globalFilter,
      rowSelection,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: selectableRows,
    enableSorting: true,
    manualSorting: isSortedManually,
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enablePagination: false,
  });

  const isInitialRender = React.useRef(true);

  React.useEffect(() => {
    if (setSortParams) {
      const sortParams = {
        sortBy: sorting[0]?.id || "",
        sortDir: sorting[0]?.desc ? "desc" : "asc",
      };
      setSortParams(sortParams);
    }
  }, [sorting]);

  React.useEffect(() => {
    if (onSelectedRowsChange && data && data.length > 0) {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }

      const selectedRowIds = Object.keys(rowSelection);
      const selectedData = selectedRowIds.map((id) => data[parseInt(id)]);
      onSelectedRowsChange({ selectedRows: selectedData });
    }
  }, [rowSelection, onSelectedRowsChange, data]);

  const getSortIcon = (column) => {
    if (!column.getCanSort()) return null;

    const isSorted = column.getIsSorted();

    return (
      <div className="sort-icon-container">
        {isSorted === "asc" && (
          <FontAwesomeIcon icon={faCaretUp} className="sort-icon active" />
        )}
        {isSorted === "desc" && (
          <FontAwesomeIcon icon={faCaretDown} className="sort-icon active" />
        )}
        {!isSorted && <div className="sort-icon-placeholder" />}
      </div>
    );
  };

  const getRowIdentity = (row) => row?.[rowIdKey] ?? row?.id;

  const handleDragEnd = (event) => {
    if (!onRowsReorder) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const visibleRows = table.getRowModel().rows.map((row) => row.original);
    const oldIndex = visibleRows.findIndex(
      (item) => getRowIdentity(item) === active.id
    );
    const newIndex = visibleRows.findIndex(
      (item) => getRowIdentity(item) === over.id
    );

    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedRows = arrayMove([...visibleRows], oldIndex, newIndex);
    onRowsReorder(reorderedRows, {
      activeId: active.id,
      overId: over.id,
      oldIndex,
      newIndex,
    });
  };

  const tableContent = (
    <div className="data-table-container">
      <div className="data-table-header-container">
        <table className={`data-table ${truncate ? "is-truncated" : ""}`}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {selectableRows && (
                  <th className="data-table-header data-table-checkbox-header">
                    <input
                      type="checkbox"
                      checked={table.getIsAllPageRowsSelected()}
                      onChange={table.getToggleAllPageRowsSelectedHandler()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                )}
                {headerGroup.headers.map((header) => {
                  const originalColumn = columns.find(
                    (col) =>
                      (col.id ||
                        col.name?.toLowerCase().replace(/\s+/g, "_")) ===
                      header.column.id
                  );
                  const canSort = header.column.getCanSort();

                  return (
                    <th
                      key={header.id}
                      className={`data-table-header ${
                        canSort ? "sortable" : ""
                      } ${header.id.replaceAll("_", "-")}-header`}
                      style={{
                        width: truncate ? undefined : header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        padding:
                          originalColumn?.id === "project_color"
                            ? "0"
                            : undefined,
                        ...originalColumn?.style,
                      }}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div className="header-content">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {getSortIcon(header.column)}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
        </table>
      </div>

      <div className="data-table-body-container">
        <table className={`data-table ${truncate ? "is-truncated" : ""}`}>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              <SortableContext
                items={table
                  .getRowModel()
                  .rows.map((row) => getRowIdentity(row.original))}
                strategy={verticalListSortingStrategy}
              >
                {table.getRowModel().rows.map((row) => {
                  const rowClassName = `data-table-row ${
                    highlightOnHover ? "highlight-on-hover" : ""
                  } ${pointerOnHover ? "pointer-on-hover" : ""} ${
                    onRowClick ? "clickable" : ""
                  }`;

                  if (!draggableRows) {
                    return (
                      <tr
                        key={row.id}
                        className={rowClassName}
                        onClick={() => onRowClick && onRowClick(row.original)}
                      >
                        {selectableRows && (
                          <td className="data-table-cell data-table-checkbox">
                            <input
                              type="checkbox"
                              checked={row.getIsSelected()}
                              onChange={row.getToggleSelectedHandler()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        )}
                        {row.getVisibleCells().map((cell) => {
                          const originalColumn = columns.find(
                            (col) =>
                              (col.id ||
                                col.name
                                  ?.toLowerCase()
                                  .replace(/\s+/g, "_")) === cell.column.id
                          );

                          return (
                            <td
                              key={cell.id}
                              className={`data-table-cell ${cell.column.id.replaceAll(
                                "_",
                                "-"
                              )}-cell`}
                              data-fulltext={cell.getValue()}
                              style={{
                                width: truncate
                                  ? undefined
                                  : cell.column.getSize(),
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                                position:
                                  originalColumn?.id === "project_color"
                                    ? "relative"
                                    : "undefined",
                                padding:
                                  originalColumn?.id === "project_color"
                                    ? "0"
                                    : undefined,
                                ...originalColumn?.style,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }

                  return (
                    <SortableDataRow
                      key={row.id}
                      rowId={getRowIdentity(row.original)}
                      className={rowClassName}
                      onClick={() => onRowClick && onRowClick(row.original)}
                    >
                      {({ dragHandleProps }) => (
                        <>
                          {selectableRows && (
                            <td className="data-table-cell data-table-checkbox">
                              <input
                                type="checkbox"
                                checked={row.getIsSelected()}
                                onChange={row.getToggleSelectedHandler()}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                          )}
                          {row.getVisibleCells().map((cell) => {
                            const originalColumn = columns.find(
                              (col) =>
                                (col.id ||
                                  col.name
                                    ?.toLowerCase()
                                    .replace(/\s+/g, "_")) === cell.column.id
                            );

                            const isDragHandleColumn =
                              cell.column.id === "drag_handle";
                            const cellContent =
                              isDragHandleColumn && originalColumn?.cell
                                ? originalColumn.cell(row.original, {
                                    dragHandleProps,
                                  })
                                : flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  );

                            return (
                              <td
                                key={cell.id}
                                className={`data-table-cell ${cell.column.id.replaceAll(
                                  "_",
                                  "-"
                                )}-cell`}
                                data-fulltext={cell.getValue()}
                                style={{
                                  width: truncate
                                    ? undefined
                                    : cell.column.getSize(),
                                  minWidth: cell.column.columnDef.minSize,
                                  maxWidth: cell.column.columnDef.maxSize,
                                  position:
                                    originalColumn?.id === "project_color"
                                      ? "relative"
                                      : "undefined",
                                  padding:
                                    originalColumn?.id === "project_color"
                                      ? "0"
                                      : undefined,
                                  ...originalColumn?.style,
                                }}
                              >
                                {cellContent}
                              </td>
                            );
                          })}
                        </>
                      )}
                    </SortableDataRow>
                  );
                })}
              </SortableContext>
            ) : (
              <tr>
                <td
                  colSpan={
                    selectableRows
                      ? tableColumns.length + 1
                      : tableColumns.length
                  }
                  className="data-table-no-data"
                >
                  {loading ? (
                    <div className="data-table-loading">
                      <Loading />
                    </div>
                  ) : (
                    noDataComponent
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {baseUrl && recordsData?.totalPages > 1 && onPageChange && (
        <div className="data-table-pagination">
          <PaginationButtons
            currentPage={recordsData.currentPage}
            totalPages={recordsData.totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );

  if (!draggableRows) {
    return tableContent;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {tableContent}
    </DndContext>
  );
};

export default DataTable;
